import { Button } from "@/components/flexkit/Button";
import { CopyableInput } from "@/components/podkit/forms/CopyableInput";
import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Heading3, Subheading } from "@/components/podkit/typography/Headings";
import { TierOptions } from "@/components/tiering/TierOptions";
import { useOnBlurError } from "@/hooks/use-onblur-error";
import { useOrganization, useUpdateOrganization, type PlainOrganization } from "@/queries/organization-queries";
import { OCUs } from "@/routes/settings/organization/billing/OCU";
import { DeleteOrganizationModal } from "@/routes/settings/organization/manage-organization/DeleteOrganizationModal";
import { formatError } from "@/utils/errors";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";
import { useCallback, useEffect, useId, useState, type FC, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";

export const OrganizationSettings: FC = () => {
    const { data: organization, isLoading } = useOrganization();

    if (!organization || isLoading) {
        return <LoadingState />;
    }

    return (
        <div className="flex flex-col gap-8">
            <OrganizationDetailsCard />
            <hr className="border-border-subtle" />
            <OrganizationDeleteCard org={organization} />
        </div>
    );
};

const orgNameSchema = z.string().min(3, "Display name must be at least 3 characters");

const OrganizationDetailsCard: FC = () => {
    const { toast } = useToast();

    const { data: orgData } = useOrganization();

    const inputNameID = useId();
    const inputOrgID = useId();
    const [organizationName, setOrganizationName] = useState<string | undefined>();
    const updateOrganization = useUpdateOrganization();

    useEffect(() => {
        setOrganizationName(orgData?.name);
    }, [orgData]);

    const handleUpdateName = useCallback(
        (evt: FormEvent<HTMLFormElement>) => {
            if (!orgData) {
                return;
            }
            evt.preventDefault();

            if (!organizationName) {
                toast({ title: "Organization name cannot be empty" });
                return;
            }

            updateOrganization.mutate(
                { name: organizationName },
                {
                    onSuccess: () => {
                        toast({ title: "Updated display name" });
                    },
                    onError: (err) => {
                        toast({ title: "Failed to update display name", description: formatError(err) });
                    },
                },
            );
        },
        [orgData, organizationName, updateOrganization, toast],
    );

    const showBillingLink = orgData?.tier !== OrganizationTier.ENTERPRISE && orgData?.tier !== OrganizationTier.FREE;
    const { message, onBlur: onBlurName } = useOnBlurError(orgNameSchema, organizationName || "");

    return (
        <>
            <form onSubmit={handleUpdateName}>
                <InputField label="Display name" id={inputNameID} error={message}>
                    <Input
                        data-testid="organization_name"
                        id={inputNameID}
                        type="text"
                        name="organization_name"
                        value={organizationName || ""}
                        onChange={(e) => setOrganizationName(e.target.value)}
                        onBlur={onBlurName}
                    />
                </InputField>

                <Button
                    variant={"secondary"}
                    className="mt-3"
                    type="submit"
                    loading={updateOrganization.isPending}
                    data-tracking-id="update-organization-organization-settings"
                >
                    Update
                </Button>
            </form>

            <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                    <span className="text-base font-bold text-content-primary">Tier</span>
                </div>

                <TierOptions currentTier={orgData?.tier} />

                {showBillingLink && (
                    <div className="flex gap-1 text-base text-content-secondary">
                        Under
                        <Link to="/settings/billing" className="underline">
                            Billing
                        </Link>
                        you can manage your tier and <OCUs />.
                    </div>
                )}
            </div>

            <div className="flex flex-col gap-3">
                <InputField label="Organization ID" id={inputOrgID}>
                    <div className="max-w-[600px]">
                        <CopyableInput
                            className="w-full"
                            value={orgData?.id || ""}
                            copyable
                            title="Copy organization ID"
                            id={inputOrgID}
                            data-tracking-id="copy-organization-id-organization-settings"
                            disabled
                        />
                    </div>
                </InputField>
            </div>
        </>
    );
};

type OrganizationDeleteCardProps = {
    org: PlainOrganization;
};

const OrganizationDeleteCard: FC<OrganizationDeleteCardProps> = ({ org }) => {
    const [showModal, setShowModal] = useState(false);
    const navigate = useNavigate();
    const onDeletion = useCallback(() => {
        // Move us away from the organization settings page
        navigate("/", { replace: true });
    }, [navigate]);

    return (
        <div className="space-y-4">
            <div className="space-y-1">
                <Heading3>Delete organization</Heading3>
                <Subheading>
                    This permanently removes the organization and everything within it, including members and resources.
                </Subheading>
                <Subheading>You won&apos;t be able to restore the organization once it&apos;s deleted.</Subheading>
            </div>

            <Button
                type="button"
                variant={"destructive-secondary"}
                onClick={() => setShowModal(true)}
                data-tracking-id="delete-organization-organization-settings"
            >
                Delete this Organization
            </Button>

            {showModal && (
                <DeleteOrganizationModal org={org} onContinue={onDeletion} onClose={() => setShowModal(false)} />
            )}
        </div>
    );
};
